/**
 * MetricsDashboard Component
 *
 * Grid layout of MetricsCard components showing classification statistics.
 * Displays automation rate prominently with breakdowns by classification method.
 */

import {
  Zap,
  CheckCircle,
  Sparkles,
  UserCheck,
  AlertCircle,
  Clock,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
} from 'lucide-react';
import type { ClassificationMetrics } from '@/types/accounting';
import { MetricsCard } from './MetricsCard';

export interface MetricsDashboardProps {
  /** Classification metrics data */
  metrics: ClassificationMetrics;
}

/**
 * Format a number as a percentage
 */
function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

/**
 * Format a number with commas
 */
function formatNumber(value: number): string {
  return value.toLocaleString();
}

/**
 * MetricsDashboard displays a comprehensive view of classification statistics.
 *
 * @example
 * <MetricsDashboard metrics={classificationMetrics} />
 */
export function MetricsDashboard({ metrics }: MetricsDashboardProps) {
  // Calculate percentages for the breakdown
  const total = metrics.totalTransactions || 1; // Avoid division by zero
  const rulePercent = (metrics.ruleMatched / total) * 100;
  const llmPercent = (metrics.llmClassified / total) * 100;
  const manualPercent = (metrics.manuallyClassified / total) * 100;
  const pendingPercent = (metrics.pending / total) * 100;
  const reviewPercent = (metrics.needsReview / total) * 100;

  return (
    <div className="space-y-6" data-testid="metrics-dashboard">
      {/* Primary Metric - Automation Rate */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-green-800 uppercase tracking-wider">
              Automation Rate
            </p>
            <p className="text-5xl font-bold text-green-700 mt-2" data-testid="automation-rate">
              {formatPercent(metrics.automationRate)}
            </p>
            <p className="text-sm text-green-600 mt-2">
              {formatNumber(metrics.ruleMatched + metrics.llmClassified)} of {formatNumber(metrics.totalTransactions)} transactions
              classified automatically
            </p>
          </div>
          <div className="hidden sm:block">
            <Zap className="w-16 h-16 text-green-300" />
          </div>
        </div>
      </div>

      {/* Transaction Breakdown */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
          Classification Breakdown
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <MetricsCard
            label="Rule Matched"
            value={formatNumber(metrics.ruleMatched)}
            description={formatPercent(rulePercent)}
            icon={CheckCircle}
            iconColor="green"
          />
          <MetricsCard
            label="AI Classified"
            value={formatNumber(metrics.llmClassified)}
            description={formatPercent(llmPercent)}
            icon={Sparkles}
            iconColor="blue"
          />
          <MetricsCard
            label="Manual"
            value={formatNumber(metrics.manuallyClassified)}
            description={formatPercent(manualPercent)}
            icon={UserCheck}
            iconColor="purple"
          />
          <MetricsCard
            label="Needs Review"
            value={formatNumber(metrics.needsReview)}
            description={formatPercent(reviewPercent)}
            icon={AlertCircle}
            iconColor="amber"
          />
          <MetricsCard
            label="Pending"
            value={formatNumber(metrics.pending)}
            description={formatPercent(pendingPercent)}
            icon={Clock}
            iconColor="gray"
          />
        </div>
      </div>

      {/* AI Performance */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
          AI Performance
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricsCard
            label="Avg Confidence"
            value={metrics.avgLlmConfidence ? formatPercent(metrics.avgLlmConfidence * 100) : 'N/A'}
            icon={Sparkles}
            iconColor="blue"
          />
          <MetricsCard
            label="Rules Pending"
            value={formatNumber(metrics.suggestedRulesPending)}
            icon={HelpCircle}
            iconColor="amber"
          />
          <MetricsCard
            label="Rules Approved"
            value={formatNumber(metrics.suggestedRulesApproved)}
            icon={ThumbsUp}
            iconColor="green"
          />
          <MetricsCard
            label="Rules Rejected"
            value={formatNumber(metrics.suggestedRulesRejected)}
            icon={ThumbsDown}
            iconColor="red"
          />
        </div>
      </div>

      {/* Visual Breakdown Bar */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
          Visual Breakdown
        </h3>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="h-6 rounded-full overflow-hidden flex bg-gray-100">
            {metrics.ruleMatched > 0 && (
              <div
                className="bg-green-500 transition-all"
                style={{ width: `${rulePercent}%` }}
                title={`Rule Matched: ${formatNumber(metrics.ruleMatched)} (${formatPercent(rulePercent)})`}
              />
            )}
            {metrics.llmClassified > 0 && (
              <div
                className="bg-blue-500 transition-all"
                style={{ width: `${llmPercent}%` }}
                title={`AI Classified: ${formatNumber(metrics.llmClassified)} (${formatPercent(llmPercent)})`}
              />
            )}
            {metrics.manuallyClassified > 0 && (
              <div
                className="bg-purple-500 transition-all"
                style={{ width: `${manualPercent}%` }}
                title={`Manual: ${formatNumber(metrics.manuallyClassified)} (${formatPercent(manualPercent)})`}
              />
            )}
            {metrics.needsReview > 0 && (
              <div
                className="bg-amber-500 transition-all"
                style={{ width: `${reviewPercent}%` }}
                title={`Needs Review: ${formatNumber(metrics.needsReview)} (${formatPercent(reviewPercent)})`}
              />
            )}
            {metrics.pending > 0 && (
              <div
                className="bg-gray-400 transition-all"
                style={{ width: `${pendingPercent}%` }}
                title={`Pending: ${formatNumber(metrics.pending)} (${formatPercent(pendingPercent)})`}
              />
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-green-500" />
              <span className="text-gray-600">Rule Matched</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-blue-500" />
              <span className="text-gray-600">AI Classified</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-purple-500" />
              <span className="text-gray-600">Manual</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-amber-500" />
              <span className="text-gray-600">Needs Review</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-gray-400" />
              <span className="text-gray-600">Pending</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MetricsDashboard;
